/**
 * Cross-org custodian access (org-switcher) — frontend state.
 *
 * A platform admin can "act as" another organization: full ("god mode") access,
 * scoped to that org. The choice is stored in localStorage and sent to the API
 * as the `X-Acting-Org` header via a single fetch interceptor, so every request
 * path is covered without touching each caller. The backend verifies the caller
 * really is a platform admin and grants full access — this is UI plumbing, not
 * the gate.
 */
import { config } from '../config';

const ORG_ID_KEY = 'acting_org_id';
const ORG_NAME_KEY = 'acting_org_name';

export interface ActingOrg {
  id: string;
  name: string;
}

/** The org currently being acted as, or null when in the home org. */
export function getActingOrg(): ActingOrg | null {
  const id = localStorage.getItem(ORG_ID_KEY);
  if (!id) return null;
  return { id, name: localStorage.getItem(ORG_NAME_KEY) || id };
}

/** Start acting as an org, then hard-reload into a clean context so every query
 *  refetches scoped to it (avoids mixing home + target data in caches). */
export function startActingAs(org: ActingOrg): void {
  localStorage.setItem(ORG_ID_KEY, org.id);
  localStorage.setItem(ORG_NAME_KEY, org.name);
  // Land on the capability-aware index: while acting (god mode) the profile has
  // monitoring.view, so it resolves to Today.
  window.location.assign('/v4');
}

/** Clear the acting org WITHOUT reloading. Called on auth transitions
 *  (login/logout) so a stale acting org can never bleed across sessions or
 *  users on a shared browser. */
export function clearActingOrgStorage(): void {
  localStorage.removeItem(ORG_ID_KEY);
  localStorage.removeItem(ORG_NAME_KEY);
}

/** Return to the home org (exit custodian mode) and reload. */
export function stopActingAs(): void {
  clearActingOrgStorage();
  // Back to the home org — via the capability-aware index, since the home
  // context may not have monitoring.view (e.g. a platform admin).
  window.location.assign('/v4');
}

/** True when `url` targets our API — matched by origin + path prefix (not a raw
 *  string prefix, which could match a look-alike host). */
function isApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const target = new URL(url, window.location.origin);
    const api = new URL(config.API_URL, window.location.origin);
    return target.origin === api.origin && target.pathname.startsWith(api.pathname);
  } catch {
    return url.startsWith(config.API_URL);
  }
}

let installed = false;

/**
 * Install a one-time `window.fetch` interceptor that adds the `X-Acting-Org`
 * header to our API requests while acting as another org. A single choke point
 * so every request path (there are many, several bypass the shared transport)
 * is covered. No-op when not acting or for non-API URLs.
 */
export function installActingOrgInterceptor(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const actingId = localStorage.getItem(ORG_ID_KEY);
    if (!actingId) return original(input, init);

    const url =
      typeof input === 'string' ? input
      : input instanceof URL ? input.href
      : input.url;
    if (!isApiUrl(url)) return original(input, init);

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set('X-Acting-Org', actingId);
    const response = await original(input, { ...init, headers });

    // The backend rejects with a distinctive 403 when the acting org is no
    // longer available (lost platform admin, or org deleted) rather than
    // silently running in the home org. Detect it, exit acting mode, and reload
    // so the UI stops claiming access it no longer has.
    if (response.status === 403 && localStorage.getItem(ORG_ID_KEY)) {
      try {
        const body = await response.clone().json();
        if (body?.detail?.error === 'acting_org_unavailable') {
          clearActingOrgStorage();
          window.location.assign('/v4');
        }
      } catch {
        /* not our acting-org 403 — leave the response untouched */
      }
    }
    return response;
  };
}
